import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ArticleForm.css";
import DOMPurify from "dompurify";
import { postArticle, EditArticleDetail } from "../../api/ArticleApi/Article";
import imageCompression from "browser-image-compression";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { routePath } from "../../app/routes/routepath";
import CdnEditor from "../../app/components/CkEditorCustom/CdnEditor";
import Feedback from "../../app/components/FeedbackModal/Feedback";
import TechnologySelector from "../../app/components/TechnologySelector/TechnologySelector";

export default function EditArticle() {
  const location = useLocation();
  const EditArticleData = location.state;
  const [isProcessing, setisProcessing] = useState(null);
  const navigate = useNavigate();

  const [authorId, setAuthorId] = useState();
  const [roleId, setRoleId] = useState();
  const [submitResponse, setSubmitResponse] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [readyToEdit, setreadyToEdit] = useState(false);
  const prefillDoneRef = useRef(false);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const [finalData, setFinalData] = useState({
    authorId: null,
    roleId: null,
    articleTitle: "",
    articleContent: "",
    articleTechnologies: [],
    articleDescription: "",
    articleCoverImage: null,
    actionType: "",
    articleId: "",
  });

  useEffect(() => {
    if (localStorage.getItem("token") !== null) {
      const id = parseInt(localStorage.getItem("UserId"));
      const role = parseInt(localStorage.getItem("RoleId"));
      setAuthorId(id);
      setRoleId(role);
      setFinalData((prev) => ({ ...prev, authorId: id, roleId: role }));
    } else {
      toast.error("Please login");
      navigate(routePath.login);
    }
  }, []);

  /* Pre-fill static fields when edit data is available */
  useEffect(() => {
    if (EditArticleData) {
      setFinalData((prev) => ({
        ...prev,
        articleId: EditArticleData.articleId,
        articleTitle: EditArticleData.articleTitle || "",
        articleContent: EditArticleData.articleContent || "",
        articleDescription: EditArticleData.articleDescription || "",
        articleCoverImage: EditArticleData.articleCoverImage || null,
        actionType: "",
      }));
      setreadyToEdit(true);
    }
  }, [EditArticleData]);

  /**
   * Called by TechnologySelector once all technologies are fetched.
   * Uses the flat list to resolve technology names → IDs for pre-fill.
   * Runs only once (guarded by prefillDoneRef).
   */
  const handleTechListReady = useCallback(
    (flatList) => {
      if (prefillDoneRef.current || !EditArticleData?.technology) return;
      prefillDoneRef.current = true;

      // Try matching by name first (comma-separated names from API)
      const techNames = EditArticleData.technology
        .split(",")
        .map((n) => n.trim())
        .filter(Boolean);

      const matchedByName = flatList
        .filter((t) => techNames.includes(t.technologyName))
        .map((t) => t.technologyId);

      if (matchedByName.length > 0) {
        setSelectedOptions(matchedByName);
        return;
      }

      // Fallback: try treating the values as numeric IDs
      const matchedByIds = techNames
        .map((v) => parseInt(v, 10))
        .filter((n) => !isNaN(n));
      if (matchedByIds.length > 0) {
        setSelectedOptions(matchedByIds);
      }
    },
    [EditArticleData],
  );

  /* Sync selectedOptions → finalData */
  useEffect(() => {
    setFinalData((prev) => ({
      ...prev,
      articleTechnologies: selectedOptions,
    }));
  }, [selectedOptions]);

  const handleTitleChange = (e) =>
    setFinalData((prev) => ({ ...prev, articleTitle: e.target.value }));

  const handleContentChange = (value) =>
    setFinalData((prev) => ({ ...prev, articleContent: value }));

  const handleDescriptionChange = (e) =>
    setFinalData((prev) => ({ ...prev, articleDescription: e.target.value }));

  const handleCoverImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size cannot exceed 20 MB");
      event.target.value = ""; // optional: clear input
      return;
    }
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      const base64 = await convertToBase64(compressedFile);
      setFinalData((prev) => ({ ...prev, articleCoverImage: base64 }));
    } catch (error) {
      console.error("Compression or conversion error:", error);
    }
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const sanitizeInputs = () => {
    const cleanHTML = DOMPurify.sanitize(finalData.articleContent, {
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
      FORBID_TAGS: ["iframe", "object", "embed"],
    });
    return {
      ...finalData,
      articleTitle: DOMPurify.sanitize(finalData.articleTitle),
      articleDescription: DOMPurify.sanitize(finalData.articleDescription),
      articleContent: cleanHTML,
      articleTechnologies: finalData.articleTechnologies,
      articleCoverImage: finalData.articleCoverImage,
      authorId: finalData.authorId,
      roleId: finalData.roleId,
      articleId: finalData.articleId,
    };
  };

  const validateForm = () => {
    if (
      !finalData.articleTitle.trim() ||
      !finalData.articleContent.trim() ||
      selectedOptions.length === 0 ||
      !finalData.articleDescription.trim() ||
      !finalData.articleCoverImage
    ) {
      toast.error("Please fill all the required fields.");
      return false;
    }
    return true;
  };

  const handleArticleUpdation = async () => {
    if (!validateForm()) return;
    const updatedData = { ...sanitizeInputs(), actionType: 5 };
    setisProcessing("Publish Article");
    try {
      const response = await EditArticleDetail(updatedData);
      setSubmitResponse(response.result === 1);
      setFeedback({
        open: true,
        type: "success",
        title: "Success",
        message: "Your article has been updated successfully.",
      });
    } catch (error) {
      console.error("Submission Error:", error);
      setSubmitResponse(false);
      setFeedback({
        open: true,
        type: "error",
        title: "Failed",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setisProcessing(null);
    }
  };

  const handleDraftedArticleUpdation = async () => {
    if (!validateForm()) return;
    const updatedData = { ...sanitizeInputs(), actionType: 6 };
    setisProcessing("Save as Draft");
    try {
      const response = await EditArticleDetail(updatedData);
      setSubmitResponse(response.result === 1);
      setFeedback({
        open: true,
        type: "success",
        title: "Success",
        message: "Your article has been saved as a draft successfully.",
      });
    } catch (error) {
      console.error("Draft Error:", error);
      setSubmitResponse(false);
      setFeedback({
        open: true,
        type: "error",
        title: "Failed",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setisProcessing(null);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <>
      <div className="role-box">
        <ul className="breadcumb">
          <li className="active" onClick={handleBack}>
            <i className="bi bi-file-earmark-richtext"></i>
            Article
          </li>
          <li className="dash-arrow">/</li>
          <li>
            <span>Edit Article</span>
          </li>
        </ul>
      </div>

      <div>
        <div className="main_widget">
          <div className="w-100 border-radius-6">
            <div className="form-header">
              <h2>Edit Your Article</h2>
            </div>
            <div className="p-20">
              {/* Title */}
              <div className="blog_form_Wraper">
                <label>
                  Title<sup>*</sup>
                </label>
                <div className="input_box">
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder="Enter article title (max 80 characters)"
                      value={finalData.articleTitle}
                      className="form-control"
                      maxLength={80}
                      onChange={handleTitleChange}
                      style={{ paddingRight: "55px" }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "12px",
                        color:
                          finalData.articleTitle.length >= 80 ? "red" : "#999",
                        pointerEvents: "none",
                      }}
                    >
                      {finalData.articleTitle.length}/80
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="blog_form_Wraper">
                <label>
                  Content<sup>*</sup>
                </label>
                <div className="input_box">
                  {readyToEdit && (
                    <CdnEditor
                      value={finalData.articleContent}
                      onChange={handleContentChange}
                    />
                  )}
                </div>
              </div>

              {/* Tags / Technology Selector */}
              <div className="blog_form_Wraper">
                <label>
                  Tags<sup>*</sup>
                </label>
                <TechnologySelector
                  selectedIds={selectedOptions}
                  onChange={setSelectedOptions}
                  maxSelections={5}
                  onTechListReady={handleTechListReady}
                />
              </div>

              {/* Summary */}
              <div className="blog_form_Wraper">
                <label>
                  Summary<sup>*</sup>
                </label>
                <div className="input_box">
                  <input
                    type="text"
                    placeholder="Keywords"
                    className="form-control"
                    onChange={handleDescriptionChange}
                    value={finalData.articleDescription}
                  />
                </div>
              </div>

              {/* Cover Image */}
              <div className="blog_form_Wraper">
                <label>
                  Upload Cover Image<sup>*</sup>
                </label>
                <div className="upload-container">
                  <h2>Upload Your File</h2>
                  <label htmlFor="file-upload" className="custom-file-upload">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path
                        d="M12 16v-8M8 12l4-4 4 4M4 20h16"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Choose File
                  </label>
                  <p class="limit-text">Maximum upload size 20 MB</p>
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleCoverImageChange}
                  />
                  <br />
                  {finalData?.articleCoverImage && (
                    <img
                      src={finalData.articleCoverImage}
                      style={{
                        width: 300,
                        height: 150,
                        borderRadius: 10,
                        marginTop: "20px",
                      }}
                      alt="article Cover"
                    />
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="blog_form_btn_wraper">
                <button
                  className="delete_approve_btn"
                  onClick={handleDraftedArticleUpdation}
                  disabled={isProcessing === "Save as Draft"}
                >
                  {isProcessing === "Save as Draft" ? (
                    <div className="draft_spinner"></div>
                  ) : (
                    "Save as Draft"
                  )}
                </button>
                <button
                  className="delete_reject_btn"
                  onClick={handleArticleUpdation}
                  disabled={isProcessing === "Publish Article"}
                >
                  {isProcessing === "Publish Article" ? (
                    <div className="Publish_spinner"></div>
                  ) : (
                    "Publish Article"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Feedback
        isOpen={feedback.open}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={() => {
          setFeedback({ ...feedback, open: false });
          navigate(routePath.AdminArticle);
        }}
      />
    </>
  );
}
